import { Injectable } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { map, switchMap, } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { of } from 'rxjs';
import { GetUserOnBehalfOfResponse } from '../../core/models/user-roles-permission.model';
import { HttpClient } from '@angular/common/http';

export const CLIENT_MANAGER_PARAM = 'client-manager';

@Injectable({
    providedIn: 'root'
})
// Stores state information related to user impersonation.
// Provides methods for enabling and disabling Client Manager View and SME View
export class ImpersonationService {
    // Observable stream which indicates whether Client Manager View is enabled by consuming the viewAs queryParam
    clientManagerModeActive$ = this.route.queryParams.pipe(
        map((params) => {
            const viewAs = parseViewAs(params, window.location.toString())
            return viewAs === CLIENT_MANAGER_PARAM
        })
    );

    // Observable stream which indicates whether SME View is enabled by consuming the viewAs queryParam
    // returns -1 if disabled, or the value of viewAs parsed to a number if enabled
    private viewAsSmeUserId$ = this.route.queryParams.pipe(
        map((params) => {
            const viewAs = parseInt(params['viewAs']);
            // if value is not NaN, assume it is a userId and impersonation is active
            return isNaN(viewAs) ? -1 : viewAs;
        })
    );

    // Observable stream of the impersonated SME User
    // Returns null if disabled, or the user details if enabled
    smeUserDetails$ = this.viewAsSmeUserId$.pipe(
        switchMap((userId) => {
            if (userId > -1) {
                return this.getUserDetails(userId).pipe(
                    map(({ result }) => result)
                )
            } else {
                return of(null)
            }
        })
    );

    // NOTE: studyId should be set by the StudyIdResolver; Do Not Use elsewhere;
    studyId = -1;

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private _http: HttpClient,
    ) {

    }

    /**
     * Activates Client Manager View - adds a query param 'viewAs=client-manager' and reloads the current route
     */
    activateClientManagerView() {
        if (!environment.production) {
            if (this.studyId === -1) {
                console.error('ImpersonationService: StudyId was not set properly. Make sure to use the StudyIdResolver to set the studyId');
            }
        }

        this.router.navigate(
            [`/study/${this.studyId}/surveys/wage-survey-list`],
            {
                queryParams: {
                    viewAs: CLIENT_MANAGER_PARAM,
                },
                queryParamsHandling: 'merge',
            }
        );

    }

    deactivateClientManagerView() {
        const queryParams = window.location.href.substring(window.location.href.lastIndexOf('/') + 1);
        const surveyId = parseInt(queryParams.substring(0, queryParams.indexOf('?')));
        const isExpenseSurveyUrl = window.location.href === `${environment.redirectUri}study/${this.studyId}/surveys/expense-survey/${surveyId}?viewAs=client-manager`;
        if (this.studyId > -1 && !isExpenseSurveyUrl) {
            this.router.navigate([`/study/${this.studyId}/home`]);
        }
        else if (this.studyId > -1 && isExpenseSurveyUrl) {
            this.router.navigate([`/study/${this.studyId}/surveys/wage-survey-list`]);
        }
        else {
            this.router.navigate(['/'])
        }
    }
    

    deactivateSmeView() {
        this.router.navigate([`/study/${this.studyId}/surveys/wage-survey-list`])
    }

    private getUserDetails(userId: number) {
        const params = {
            userId,
        };

        return this._http.get<GetUserOnBehalfOfResponse>(`${environment.webApiDefaultRoot}User/GetUserDetailOnBehalfOf`, { params });
    }
}

// parses the viewAs parameter. They are not available on app load, so we need to manually parse the url for deep linking
function parseViewAs(params: Params, url: string) {
    const viewAsIndex = url.indexOf('viewAs');
    const startIndex = viewAsIndex + 7;
    const nextParamIndex = url.indexOf('&', startIndex + 1);

    const endIndex = nextParamIndex === -1
        ? url.length
        : nextParamIndex;

    const viewAs = (viewAsIndex > -1)
        ? url.substring(startIndex, endIndex)
        : '';

    // if the params object exists, use that, otherwise use the value we parsed
    return params['viewAs'] ?? viewAs;
}
